import React, { useEffect } from "react";
import {
  Modal,
  TextField,
  Button,
  IconButton,
  Autocomplete,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import {
  getAbbreviationsInfo,
  updateAbbreviation,
  getBadDataInfo,
  updateBadData,
  getSingledoubleInfo,
  editSingleDouble,
  getInterveningInfo,
  updateIntervening,
  getAliasInfo,
  editAlias,
  getAnglicizedInfo,
  editAnglicized,
  getInitialInfo,
  editInitials,
  getNicknameInfo,
  editNickname,
  getCountryInfo,
  editCountryData,
} from "../../../Api";
import Swal from "sweetalert2";

function EditModal({ open, onClose, selectedTab, recordId, handleEditSubmit }) {
  const navigate = useNavigate();
  const [selectedAliasType, setSelectedAliasType] = React.useState("");
  const [content, setContent] = React.useState({
    record_id: "",
    name: "",
    abbreviations: "",
    alias_word: "",
    entity_name: "",
    anglicized_word: "",
    special_char: "",
    description: "",
    letter: "",
    replace_letter: "",
    initial: "",
    nick_name: "",
    word_list: "",
    country: "",
    alias_type: "",
    is_active: "Y",
  });
  const [originalContent, setOriginalContent] = React.useState({});

  // Fetch details when modal opens
  useEffect(() => {
    const fetchDetails = async () => {
      if (open && recordId) {
        try {
          Swal.fire({
            html: `<img src=${require("../../../assets/loader.gif")} style="width: 50px" alt="loader">`,
            showConfirmButton: false,
            heightAuto: false,
            customClass: { popup: "loader-swal" },
            allowOutsideClick: false,
          });

          let apiData;
          switch (selectedTab) {
            case 0: // Abbreviations
              apiData = await getAbbreviationsInfo(recordId);
              break;
            case 1: // Aliases
              apiData = await getAliasInfo(recordId);
              break;
            case 2: // Anglicized
              apiData = await getAnglicizedInfo(recordId);
              break;
            case 3: // Bad Data
              apiData = await getBadDataInfo(recordId);
              break;
            case 4: // Double letters
              apiData = await getSingledoubleInfo(recordId);
              break;
            case 5: // Initials
              apiData = await getInitialInfo(recordId);
              break;
            case 6: // Nicknames
              apiData = await getNicknameInfo(recordId);
              break;
            case 7: // Intervening
              apiData = await getInterveningInfo(recordId);
              break;
            case 8: // Countries
              apiData = await getCountryInfo(recordId);
              break;
            default:
              break;
          }

          Swal.close();

          if (apiData?.status === "S" && apiData?.result_code === 200) {
            const data = Array.isArray(apiData.result_info)
              ? apiData.result_info[0]
              : apiData.result_info;

            // Set content based on the data received
            const newContent = {
              record_id: recordId,
              name: data.name || "",
              abbreviations: data.abbreviations || "",
              alias_word: data.alias_word || "",
              entity_name: data.entity_name || data.entity_name || "",
              anglicized_word: data.anglicized_word || "",
              special_char: data.special_char || "",
              description: data.description || "",
              letter: data.letter || "",
              replace_letter: data.replace_letter || "",
              initial: data.initial || "",
              nick_name: data.nick_name || "",
              word_list: data.word_list || "",
              country: data.country || "",
              is_active: data.is_active || "Y",
              alias_type: data.alias_type || "",
            };

            setContent(newContent);
            console.log("Fetched Data:", content);
            setOriginalContent(newContent);
          } else if (
            apiData?.status === "F" &&
            (apiData?.message === "Unauthorized - Missing token" ||
              apiData?.message === "Unauthorized - Invalid token")
          ) {
            Swal.fire({
              text: "Please login with your credentials",
              icon: "warning",
              showConfirmButton: false,
              timer: 3000,
            }).then(() => {
              localStorage.clear();
              navigate("/login");
            });
          } else {
            Swal.fire({
              text: apiData?.message || "Failed to fetch details",
              icon: "warning",
              showConfirmButton: false,
              timer: 3000,
            });
          }
        } catch (error) {
          console.error("Error fetching details:", error);
          Swal.fire({
            text: "An error occurred while fetching details",
            icon: "error",
            showConfirmButton: false,
            timer: 3000,
          });
        }
      }
    };

    fetchDetails();
  }, [open, recordId, selectedTab, navigate]);

  const getModalTitle = () => {
    switch (selectedTab) {
      case 0:
        return "Edit Abbreviation";
      case 1:
        return "Edit Alias";
      case 2:
        return "Edit Anglicized";
      case 3:
        return "Edit Bad Data";
      case 4:
        return "Edit Double Letter";
      case 5:
        return "Edit Initial";
      case 6:
        return "Edit Nickname";
      case 7:
        return "Edit Intervening";
      case 8:
        return "Edit Country";
      default:
        return "Edit Record";
    }
  };

  const getFields = () => {
    switch (selectedTab) {
      case 0: // Abbreviations
        return [
          { name: "name", label: "Abbreviation", isRequired: true },
          { name: "abbreviations", label: "Replace Word", isRequired: true },
        ];
      case 1: // Aliases
        return [
          { name: "name", label: "Name", isRequired: true },
          { name: "alias_word", label: "Alias Name", isRequired: true },
        ];
      case 2: // Anglicized
        return [
          { name: "entity_name", label: "Name", isRequired: true },
          {
            name: "anglicized_word",
            label: "Anglicized Name",
            isRequired: true,
          },
        ];
      case 3: // Bad Data
        return [
          { name: "special_char", label: "Bad_Data", isRequired: true },
          // { name: "description", label: "Bad Data", isRequired: true },
        ];
      case 4: // Double letters
        return [
          { name: "letter", label: "Letter Code", isRequired: true },
          {
            name: "replace_letter",
            label: "Replace Letter Code",
            isRequired: true,
          },
        ];
      case 5: // Initials
        return [
          { name: "name", label: "Name", isRequired: true },
          { name: "initial", label: "Initials", isRequired: true },
        ];
      case 6: // Nicknames
        return [
          { name: "name", label: "Name", isRequired: true },
          { name: "nick_name", label: "Nickname", isRequired: true },
        ];
      case 7: // Intervening
        return [
          { name: "word_list", label: "Intervening Words", isRequired: true },
          // { name: "description", label: "Intervening", isRequired: true },
        ];
      case 8: // Countries
        return [{ name: "country", label: "Country Name", isRequired: true }];
      default:
        return [];
    }
  };

  const hasChanges = () => {
    const fields = getFields();
    if (selectedTab === 1) {
      const aliasTypeValue = content["alias_type"]?.trim() || "";
      const originalAliasTypeValue =
        originalContent["alias_type"]?.trim() || "";
      return fields.some((field) => {
        const currentValue = content[field.name]?.trim() || "";
        const originalValue = originalContent[field.name]?.trim() || "";
        return (
          currentValue !== originalValue ||
          aliasTypeValue !== originalAliasTypeValue
        );
      });
    }

    return fields.some((field) => {
      const currentValue = content[field.name]?.trim() || "";
      const originalValue = originalContent[field.name]?.trim() || "";
      return currentValue !== originalValue;
    });
  };

  const handleUpdate = async (e) => {
    e?.preventDefault();

    try {
      if (!hasChanges()) {
        Swal.fire({
          text: "No changes made",
          icon: "warning",
          showConfirmButton: false,
          timer: 1500,
        });
        return;
      }
      if (selectedTab === 1) {
      }
      // Validate required fields based on tab
      const fields = getFields();
      for (const field of fields) {
        if (!content[field.name]?.trim()) {
          Swal.fire({
            text: `Please enter ${field.label.toLowerCase()}`,
            icon: "warning",
            showConfirmButton: false,
            heightAuto: false,
            timer: 1500,
          });
          return;
        }
      }

      if (selectedTab == 0) {
        const name = content.name?.trim() || "";
        const abbr = content.abbreviations?.trim() || "";

        const nameHasLetter = /[a-zA-Z]/.test(name);
        const abbrHasLetter = /[a-zA-Z]/.test(abbr);

        //  Invalid if name or abbreviation has NO letters at all
        if (!nameHasLetter || !abbrHasLetter) {
          Swal.fire({
            text: "Numbers/Special characters/Combination of Numbers and Special Characters alone are not allowed.",
            icon: "warning",
            showConfirmButton: false,
            timer: 4000,
          });
          return;
        }
        if (
          content.name?.trim().toLowerCase() ===
          content.abbreviations?.trim().toLowerCase()
        ) {
          Swal.fire({
            text: "Abbreviation and Replace Word cannot be same",
            icon: "warning",
            showConfirmButton: false,
            timer: 3000,
          });
          return;
        }
      } else if (selectedTab == 1) {
        if (
          content.name?.trim().toLowerCase() ===
          content.alias_word?.trim().toLowerCase()
        ) {
          Swal.fire({
            text: "Name and Alias Name cannot be the same",
            icon: "warning",
            showConfirmButton: false,
            timer: 3000,
          });
          return;
        } else if (content.name?.trim().length < 2) {
          Swal.fire({
            text: "The Name must contain a minimum of 2 characters.",
            icon: "warning",
            showConfirmButton: false,
            timer: 3000,
          });
          return;
        } else if (content.alias_type?.trim() === "") {
          Swal.fire({
            text: "Alias Type is required",
            icon: "warning",
            showConfirmButton: false,
            timer: 3000,
          });
          return;
        }
      } else if (selectedTab === 2) {
        // Anglicized
        if (
          content.entity_name?.trim().toLowerCase() ===
          content.anglicized_word?.trim().toLowerCase()
        ) {
          Swal.fire({
            text: "Name and Anglicized Name cannot be the same",
            icon: "warning",
            showConfirmButton: false,
            timer: 3000,
          });
          return;
        }
      } else if (selectedTab === 4) {
        // Double letters
        if (
          content.letter?.trim().toLowerCase() ===
          content.replace_letter?.trim().toLowerCase()
        ) {
          Swal.fire({
            text: "Letter Code and Replace Letter Code cannot be the same",
            icon: "warning",
            showConfirmButton: false,
            timer: 3000,
          });
          return;
        } else if (
          content.letter?.trim().length ===
          content.replace_letter?.trim().length
        ) {
          Swal.fire({
            text: "If Letter Code is single letter then Replace Letter Code should be double letter or vice versa",
            icon: "warning",
            showConfirmButton: false,
            timer: 3000,
          });
          return;
        }
        const letterLower = content.letter?.trim();
        const replaceLower = content.replace_letter?.trim();

        const letterLen = letterLower.length;
        const replaceLen = replaceLower.length;
        // 3) Validate pattern like h <-> hh, a <-> aa, etc.
        // single → double OR double → single (vice versa)
        if (letterLen === 1 && replaceLen === 2) {
          // Expect replace = letter repeated twice
          if (replaceLower !== letterLower.repeat(2)) {
            Swal.fire({
              text: "Replace Letter Code must be the double of Letter Code (e.g. A → AA) and Case Sensitive. ",
              icon: "warning",
              showConfirmButton: false,
              timer: 3000,
            });
            return;
          }
        } else if (letterLen === 2 && replaceLen === 1) {
          // Expect letter = replace repeated twice
          if (letterLower !== replaceLower.repeat(2)) {
            Swal.fire({
              text: "Letter Code must be the double of Replace Letter Code (e.g. AA → A) and Case Sensitive.",
              icon: "warning",
              showConfirmButton: false,
              timer: 3000,
            });
            return;
          }
        }
      } else if (selectedTab === 5) {
        // Initials
        if (
          content.name?.trim().toLowerCase() ===
          content.initial?.trim().toLowerCase()
        ) {
          Swal.fire({
            text: "Name and Initials cannot be the same",
            icon: "warning",
            showConfirmButton: false,
            timer: 3000,
          });
          return;
        } else if (
          content.name?.trim()?.length < content.initial?.trim()?.length
        ) {
          Swal.fire({
            text: "Length of the Name should always be greater than Initial",
            icon: "warning",
            showConfirmButton: false,
            timer: 3000,
          });
          return;
        }
        // else if (/[a-z]/.test(content.initial?.trim())) {
        //   Swal.fire({
        //     text: "Lowercase letters are not allowed in Initials",
        //     icon: "warning",
        //     showConfirmButton: false,
        //     timer: 3000,
        //   });
        //   return;
        // }
        else if (
          content.name?.trim() &&
          !content.name.trim().includes(" ") &&
          content.initial?.trim()?.includes(",")
        ) {
          Swal.fire({
            text: "For a single name, multiple initials separated by commas are not allowed",
            icon: "warning",
            showConfirmButton: false,
            timer: 3000,
          });
          return;
        }
      } else if (selectedTab === 6) {
        // Nicknames
        const nickname = content.nick_name?.trim();
        const name = content.name?.trim();
        if (name?.length < 2) {
          Swal.fire({
            text: "The Name must contain a minimum of 2 characters.",
            icon: "warning",
            showConfirmButton: false,
            timer: 3000,
          });
          return;
        }
        //  else if (nickname?.length === 1 || name?.length === 1) {
        //     Swal.fire({
        //       text: "Single-letter names or nicknames are not allowed",
        //       icon: "warning",
        //       showConfirmButton: false,
        //       timer: 3000,
        //     });
        //     return;
        //   }
        else if (name?.trim().toLowerCase() === nickname?.toLowerCase()) {
          Swal.fire({
            text: "Name and Nickname cannot be the same",
            icon: "warning",
            showConfirmButton: false,
            timer: 3000,
          });
          return;
        }
      } else if (selectedTab === 7) {
        const intervening = content.word_list?.trim() || "";

        if (intervening.length < 2) {
          Swal.fire({
            text: "The Intervening Name must contain a minimum of 2 characters.",
            icon: "warning",
            showConfirmButton: false,
            timer: 3000,
          });
          return;
        }
      } else if (selectedTab === 8) {
        const country = content.country?.trim() || "";

        // Check if the first character is NOT an alphabet
        if (!/^[A-Za-z]/.test(country)) {
          Swal.fire({
            text: "Country name must start with an alphabet.",
            icon: "warning",
            showConfirmButton: false,
            timer: 3000,
          });
          return;
        }
      }
      let response;
      const baseFormData = {
        record_id: recordId,
        bank_code: localStorage.getItem("bank_code") || "",
        correl_id: localStorage.getItem("correl_id") || "",
        is_active: content.is_active || "Y",
      };

      // Only include required fields based on tab
      let formData = { ...baseFormData };
      switch (selectedTab) {
        case 0: // Abbreviations
          formData = {
            ...formData,
            name: content.name.trim(),
            abbreviations: content.abbreviations.trim(),
          };
          break;
        case 1: // Aliases
          formData = {
            ...formData,
            name: content.name.trim(),
            alias_word: content.alias_word.trim(),
            alias_type: content.alias_type.trim(),
          };
          break;
        case 2: // Anglicized
          formData = {
            ...formData,
            entity_name: content.entity_name.trim(),
            anglicized_word: content.anglicized_word.trim(),
          };
          break;
        case 3: // Bad Data
          formData = {
            ...formData,
            special_char: content.special_char.trim(),
            description: content.description.trim(),
          };
          break;
        case 4: // Double letters
          formData = {
            ...formData,
            letter: content.letter.trim(),
            replace_letter: content.replace_letter.trim(),
          };
          break;
        case 5: // Initials
          formData = {
            ...formData,
            name: content.name.trim(),
            initial: content.initial.trim(),
          };
          break;
        case 6: // Nicknames
          formData = {
            ...formData,
            name: content.name.trim(),
            nick_name: content.nick_name.trim(),
          };
          break;
        case 7: // Intervening
          formData = {
            ...formData,
            word_list: content.word_list.trim(),
            description: content.description.trim(),
          };
          break;
        case 8: // Countries
          formData = {
            ...formData,
            country: content.country.trim(),
          };
          break;
        default:
          break;
      }

      Swal.fire({
        html: `<img src=${require("../../../assets/loader.gif")} style="width: 50px" alt="loader">`,
        showConfirmButton: false,
        heightAuto: false,
        customClass: { popup: "loader-swal" },
        allowOutsideClick: false,
      });

      switch (selectedTab) {
        case 0: // Abbreviations
          response = await updateAbbreviation(formData);
          break;
        case 1: // Aliases
          response = await editAlias(formData);
          break;
        case 2: // Anglicized
          response = await editAnglicized(formData);
          break;
        case 3: // Bad Data
          response = await updateBadData(formData);
          break;
        case 4: // Double letters
          response = await editSingleDouble(formData);
          break;
        case 5: // Initials
          response = await editInitials(formData);
          break;
        case 6: // Nicknames
          response = await editNickname(formData);
          break;
        case 7: // Intervening
          response = await updateIntervening(formData);
          break;
        case 8: // Countries
          response = await editCountryData(formData);
          break;
        default:
          break;
      }

      Swal.close();

      if (response.status === "S" && response.result_code === 200) {
        Swal.fire({
          text: "Record updated successfully!",
          icon: "success",
          showConfirmButton: false,
          timer: 1500,
          heightAuto: false,
        }).then(() => {
          handleEditSubmit();
          onClose();
        });
      } else if (response?.result_code === 409) {
        Swal.fire({
          text:
            response?.message || "Data already exists. Please check your data.",
          icon: "warning",
          showConfirmButton: false,
          timer: 3000,
        });
      } else if (
        response.status === "F" &&
        (response.message === "Unauthorized - Missing token" ||
          response.message === "Unauthorized - Invalid token")
      ) {
        Swal.fire({
          text: "Please login with your credentials",
          icon: "warning",
          showConfirmButton: false,
          timer: 3000,
        }).then(() => {
          localStorage.clear();
          navigate("/login");
        });
      } else {
        Swal.fire({
          text: response.message || "Error updating record",
          icon: "warning",
          showConfirmButton: false,
          timer: 3000,
        });
      }
    } catch (error) {
      console.error("Error updating record:", error);
      Swal.fire({
        text: "Error updating record",
        icon: "error",
        showConfirmButton: false,
        timer: 1500,
        heightAuto: false,
      });
    }
  };

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="edit-modal-title">
      <div className="modal-overlay">
        <div
          className="modal-container bg-white p-4 rounded"
          style={{ width: 400, maxWidth: "90vw" }}
        >
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 id="edit-modal-title">{getModalTitle()}</h5>
            <IconButton
              onClick={onClose}
              color="black"
              sx={{ fontWeight: 600 }}
            >
              <CloseIcon />
            </IconButton>
          </div>

          {getFields().map((field, index) => (
            <TextField
              key={field.name}
              className={index > 0 ? "w-100 mt-3" : "w-100"}
              label={field.label}
              variant="outlined"
              autoComplete="off"
              required
              value={content[field.name]}
              onChange={(ev) => {
                if (selectedTab == 2) {
                  const value = ev.target.value.replace(/[^a-zA-Z]/g, "");
                  setContent({
                    ...content,
                    [field.name]: value,
                  });
                } else if (selectedTab == 4) {
                  // For Double letters, only alphabets and allow max 2 characters
                  const value = ev.target.value
                    .replace(/[^a-zA-Z]/g, "")
                    .slice(0, 2);
                  setContent({
                    ...content,
                    [field.name]: value,
                  });
                } else if (selectedTab == 5) {
                  //exculuding numbers for Initials
                  const value = ev.target.value.replace(/[0-9]/g, "");
                  setContent({
                    ...content,
                    [field.name]: value,
                  });
                } else if (selectedTab == 6) {
                  // For Nicknames, only alphabets
                  const value = ev.target.value.replace(/[^a-zA-Z-\s]/g, "");
                  setContent({
                    ...content,
                    [field.name]: value,
                  });
                } else if (selectedTab == 7) {
                  // For Intervening, only alphabets
                  const value = ev.target.value.replace(/[^a-zA-Z\s]/g, "");
                  setContent({
                    ...content,
                    [field.name]: value,
                  });
                }
                // else if (selectedTab == 8) {
                //   // For country, replace special characters and numbers
                //   const value = ev.target.value.replace(/[^a-zA-Z\s]/g, "");
                //   setContent({
                //     ...content,
                //     [field.name]: value,
                //   });
                // }
                // else {
                //   const value = ev.target.value;
                //   const invalidPattern =
                //     /<script.*?>.*?<\/script>|[<>\\{}=:/""'']/i;
                //   if (!invalidPattern.test(value)) {
                //     setContent({
                //       ...content,
                //       [field.name]: value,
                //     });
                //   }
                // }
                else {
                  const value = ev.target.value;
                  setContent({
                    ...content,
                    [field.name]: value,
                  });
                }
              }}
              inputProps={{
                maxLength: 255,
              }}
            />
          ))}
          {selectedTab === 1 && (
            <div style={{ marginBottom: 16 }}>
              <Autocomplete
                className="mt-3"
                options={["Strong", "Weak"]} // ✅ No iterator, static values
                value={content.alias_type || null}
                onChange={(_, newValue) => {
                  setContent((prev) => ({
                    ...prev,
                    alias_type: newValue || "", // 👈 Generic update
                  }));
                }}
                clearOnEscape
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Alias Type"
                    variant="outlined"
                    style={{ minWidth: 220 }}
                  />
                )}
              />
            </div>
          )}
          <div className="d-flex justify-content-end gap-2 mt-3">
            <Button variant="outlined" onClick={onClose} className="me-2">
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              onClick={handleUpdate}
            >
              Update
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default EditModal;
